import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { Checkbox, Flex, Tag } from "antd";
import { Stack } from "react-bootstrap";
import { Container } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Delete02Icon,
  Edit02Icon,
  ViewIcon,
} from "@hugeicons/core-free-icons";
import { PageHeader } from "@/CommonElements/PageTitle";
import { TableC } from "@/CommonElements/TableC";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { DATE_TIME_FORMAT } from "@/utils/constants";
import { LabelValue } from "@/Component/Value";
import {
  useDeleteNusukCardsReturnMutation,
  useGetNusukCardsReturnReasonsQuery,
  useGetNusukCardsReturnsQuery,
  usePrintNusukCardsBatchMutation,
  useUpdateNusukCardsReturnMutation,
} from "@/services/nusukCards/nusukCards";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { downloadFile } from "@/utils/helpers";
import { useSystem } from "@/utils/hooks/useLocation";
import { useNusukCardsPermissions } from "@/utils/hooks/useNusukCardsPermissions";
import ConfirmModal from "@/CommonElements/ConfirmModal";

const PAGE_SIZE = 10;
const EMPTY_ROWS = [];
const rowActionIcon = (icon) => <HugeiconsIcon icon={icon} size={16} />;

function getReturnId(row) {
  return row?.id ?? row?.return_id ?? null;
}

function getReturnBatchId(row) {
  return (
    row?.batch_id ??
    row?.nusuk_cards_batch_id ??
    row?.batch?.id ??
    row?.nusuk_cards_batch?.id ??
    null
  );
}

function getReturnBatchNo(row) {
  const no =
    row?.batch_no ?? row?.batch?.batch_no ?? row?.nusuk_cards_batch?.batch_no;
  return no != null && no !== "" ? String(no) : "";
}

function isNusukReturnReceived(row) {
  if (!row) return false;
  const num = (v) => {
    if (v === true) return 1;
    if (v === false) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  };
  for (const key of [
    "receipt_status",
    "return_receipt_status",
    "received_status",
    "return_status",
    "status",
  ]) {
    const n = num(row[key]);
    if (n === 1) return true;
    if (n === 0) return false;
  }
  if (row.is_received === true || row.is_received === 1) return true;
  if (row.is_received === false || row.is_received === 0) return false;
  return Boolean(row.received_at || row.receipt_date);
}

const NusukCardsBatchReturns = () => {
  const { t } = useTranslation();
  const { getLocalizedDate, getLocalizedText } = useLocalizedText();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch("", 500);
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  const nusukBase = `/app/${currentSystem}/nusuk-cards`;
  const { canListReturns, canUpdateReturn, canDeleteReturn, canViewExportBatchReport } =
    useNusukCardsPermissions();

  const apiParams = useMemo(
    () => ({
      page,
      per_page: PAGE_SIZE,
      ...(debouncedValue?.trim() ? { search: debouncedValue.trim() } : {}),
      ...filters,
    }),
    [page, debouncedValue, filters]
  );

  const { data, isLoading, isFetching, refetch } = useGetNusukCardsReturnsQuery(apiParams);
  const { data: returnReasons = [] } =
    useGetNusukCardsReturnReasonsQuery();
  const {isPending: isUpdatePending } =
    useUpdateNusukCardsReturnMutation();
  const { mutateAsync: deleteReturn, isPending: isDeletePending } =
    useDeleteNusukCardsReturnMutation();
  const { mutate: printNusukCardsBatch, isPending: isPrintBatchPending } =
    usePrintNusukCardsBatchMutation();

  const [selectedReturnIds, setSelectedReturnIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, row: null });

  const rows = data?.data ?? EMPTY_ROWS;
  const meta = data?.meta ?? {};
  const loading = isLoading || isFetching;
  const lastPage = Number(meta?.last_page);

  const allPageReturnIds = useMemo(
    () => rows.map((r) => String(getReturnId(r))).filter(Boolean),
    [rows]
  );

  /** Unique batch numbers for selected return rows (API print accepts batch_no list). */
  const selectedBatchNos = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const r of rows) {
      const rid = getReturnId(r);
      if (!rid || !selectedReturnIds.includes(String(rid))) continue;
      const bn = getReturnBatchNo(r);
      if (!bn || seen.has(bn)) continue;
      seen.add(bn);
      out.push(bn);
    }
    return out;
  }, [rows, selectedReturnIds]);

  const allPageSelected =
    allPageReturnIds.length > 0 &&
    allPageReturnIds.every((id) => selectedReturnIds.includes(id));
  const somePageSelected =
    !allPageSelected && allPageReturnIds.some((id) => selectedReturnIds.includes(id));

  const toggleSelectAll = useCallback(() => {
    if (allPageSelected) {
      setSelectedReturnIds((prev) => prev.filter((id) => !allPageReturnIds.includes(id)));
    } else {
      setSelectedReturnIds((prev) => [...new Set([...prev, ...allPageReturnIds])]);
    }
  }, [allPageSelected, allPageReturnIds]);

  const toggleSelectRow = useCallback((returnId) => {
    const str = String(returnId);
    setSelectedReturnIds((prev) =>
      prev.includes(str) ? prev.filter((id) => id !== str) : [...prev, str]
    );
  }, []);

  useEffect(() => {
    if (Number.isFinite(lastPage) && lastPage >= 1 && page > lastPage) {
      setPage(lastPage);
    }
  }, [lastPage, page]);

  useEffect(() => {
    setSelectedReturnIds([]);
  }, [page, debouncedValue, filters]);

  const handlePrintBatches = useCallback(
    (batchNos) => {
      if (!batchNos?.length) {
        toast.error(t("NUSUK_CARDS_BATCH_PRINT_MISSING_BATCH"));
        return;
      }
      const toastId = toast.loading(t("NUSUK_CARDS_BATCH_PRINTING"));
      printNusukCardsBatch(batchNos.map(String), {
        onSettled: () => toast.dismiss(toastId),
        onSuccess: (res) => {
          const blob = new Blob([res.data], { type: "application/pdf" });
          const label =
            batchNos.length === 1
              ? `nusuk-cards-batch-${batchNos[0]}.pdf`
              : `nusuk-cards-batches-${batchNos.length}.pdf`;
          downloadFile(blob, label);
          toast.success(t("NUSUK_CARDS_BATCH_PRINT_SUCCESS"));
        },
        onError: async (err) => {
          let message = t("NUSUK_CARDS_BATCH_PRINT_FAILED");
          const data = err?.response?.data;
          if (data instanceof Blob) {
            try {
              const parsed = JSON.parse(await data.text());
              if (parsed?.message) message = parsed.message;
            } catch {
              /* keep default */
            }
          } else if (err?.response?.data?.message) {
            message = err.response.data.message;
          }
          toast.error(message);
        },
      });
    },
    [printNusukCardsBatch, t]
  );

  const columns = useMemo(
    () => [
      {
        key: "select",
        name: (
          <Checkbox
            checked={allPageSelected}
            indeterminate={somePageSelected}
            onChange={toggleSelectAll}
          />
        ),
        filterable: false,
        render: (_, row) => {
          const returnId = getReturnId(row);
          if (!returnId) return null;
          return (
            <Checkbox
              checked={selectedReturnIds.includes(String(returnId))}
              onChange={() => toggleSelectRow(returnId)}
            />
          );
        },
      },
      {
        key: "batch_no",
        name: t("NUSUK_BATCH_NUMBER"),
        render: (_, row) =>
          row?.batch_no ?? row?.batch?.batch_no ?? row?.nusuk_cards_batch?.batch_no ?? "-",
      },
      {
        key: "campaign_id",
        name: t("CAMPAIGN_OR_GROUP_NUMBER"),
        render: (_, row) => {
          const number =
            row?.batch?.entity === "campaign" ? row?.batch?.campaign_number : row?.batch?.group_number;
          return (
            <LabelValue value={number ?? "-"} copyValue={number} buttonIconSize={12} />
          );
        },
      },
      {
        key: "service_center",
        name: t("SERVICE_CENTER"),
        render: (_, row) =>
          getLocalizedText(
            row?.batch?.service_center?.name_ar , row?.batch?.service_center?.name_en),
      },
      {
        key: "nationality",
        name: t("NATIONALITY"),
        render: (_, row) =>
          getLocalizedText(
            row?.batch?.nationality?.name_ar , row?.batch?.nationality?.name_en
          ),
      },
      {
        key: "group_name",
        name: t("GROUP_NAME"),
        render: (_, row) => row?.group_name ?? row?.batch?.group_name ?? "-",
      },
      {
        key: "card_no",
        name: t("NUSUK_CARDS_COUNT"),
        render: (_, row) => row?.card_no ?? row?.batch?.card_no ?? "-",
      },
      {
        key: "reason",
        name: t("NUSUK_RETURN_REASON_COL"),
        filterable: false,
        render: (_, row) => getLocalizedText(row?.reason?.name_ar, row?.reason?.name_en) ?? "-",
      },
      {
        key: "receipt_status",
        name: t("STATUS"),
        render: (_, row) =>
          isNusukReturnReceived(row) ? (
            <Tag color="success">{t("NUSUK_STATUS_DELIVERED")}</Tag>
          ) : (
            <Tag color="error">{t("NUSUK_STATUS_NOT_RECEIVED")}</Tag>
          ),
      },
      {
        key: "added_at",
        name: t("NUSUK_RECEIPT_DATE"),
        render: (value) => value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-",
      },
      {
        key: "representative_name",
        name: t("NUSUK_COMPLETE_CASE_RECIPIENT"),
        render: (value) => value ?? "-",
      },
      {
        key: "actions",
        name: t("ACTIONS"),
        filterable: false,
      },
    ],
    [t, getLocalizedText, getLocalizedDate, allPageSelected, somePageSelected, selectedReturnIds, toggleSelectAll, toggleSelectRow]
  );

  const filterFields = useMemo(
    () => [
      { name: "batch_number", label: t("NUSUK_BATCH_NUMBER"), type: "input" },
      {
        name: "campaign_group_number",
        label: t("CAMPAIGN_OR_GROUP_NUMBER"),
        type: "input",
        placeholder: t("CAMPAIGN_NUMBER_PLACEHOLDER"),
      },
      {
        name: "service_center_id",
        label: t("SERVICE_CENTER"),
        type: "select",
        optionKey: "service_centers",
        options: (s) => ({ value: s.id, label: s.name }),
      },
      {
        name: "nationality_id",
        label: t("NATIONALITY"),
        type: "select",
        optionKey: "nationalities",
        options: (n) => ({
          value: n.id,
          label: getLocalizedText(n.name_ar, n.name_en) || n.name,
        }),
      },
      { name: "group_name", label: t("GROUP_NAME"), type: "input" },
      { name: "batch_date", label: t("NUSUK_BATCH_DATETIME"), type: "date" },
      {
        name: "receipt_status",
        label: t("STATUS"),
        type: "select",
        options: [
          { value: "1", label: t("NUSUK_STATUS_DELIVERED") },
          { value: "0", label: t("NUSUK_STATUS_NOT_RECEIVED") },
        ],
      },
      {
        name: "reason",
        label: t("NUSUK_RETURN_REASON_COL"),
        type: "select",
        options: returnReasons.map((o) => ({ value: o.id, label: getLocalizedText(o.name_ar, o.name_en) })),
      },
    ],
    [t, getLocalizedText, returnReasons]
  );

  const getRowActions = useCallback(
    (row) => {
      const returnId = getReturnId(row);
      const batchId = getReturnBatchId(row);
      const received = isNusukReturnReceived(row);

      if (!returnId || !batchId) return [];


      const actions = [];
      if (canListReturns) {
        actions.push({
          key: "view",
          icon: rowActionIcon(ViewIcon),
          label: t("VIEW"),
          onClick: () => {
            navigate(`/app/${currentSystem}/reports/${row.id}/view`);
          },
        });
      }
      if (canUpdateReturn) {
        actions.push({
          key: "edit",
          icon: rowActionIcon(Edit02Icon),
          label: t("EDIT"),
          disabled: received || isUpdatePending,
          onClick: () =>
            navigate(`${nusukBase}/${batchId}/returns/${row.id}/edit`),
        });
      }
      if (canDeleteReturn) {
        actions.push({
          key: "delete",
          icon: rowActionIcon(Delete02Icon),
          label: t("DELETE"),
          danger: true,
          disabled: isDeletePending,
          onClick: () => setDeleteModal({ open: true, row }),
        });
      }
      return actions;
    },
    [
      t,
      navigate,
      nusukBase,
      currentSystem,
      isUpdatePending,
      isDeletePending,
      canListReturns,
      canUpdateReturn,
      canDeleteReturn,
    ]
  );

  const handleFilterApply = (next) => {
    setFilters(next);
    setPage(1);
  };
  const handleFilterReset = () => {
    setFilters({});
    setPage(1);
  };

  return (
    <div>
      <PageHeader title={t("NUSUK_CARDS_BATCH_RETURNS_SCREEN")} sticky={false} />
      <Container fluid>
        <Stack gap={3}>
          <TableC
            data={rows}
            columns={columns}
            loading={loading}
            searchValue={searchValue}
            onSearchChange={(e) => {
              setSearchValue(e.target.value);
              setPage(1);
            }}
            searchPlaceholder={t("SEARCH_HERE")}
            pagination={{
              current: meta?.current_page ?? page,
              total: meta?.total ?? 0,
              pageSize: meta?.per_page ?? PAGE_SIZE,
              onChange: setPage,
            }}
            filterFields={filterFields}
            filters={filters}
            onFilterApply={handleFilterApply}
            onFilterReset={handleFilterReset}
            actions={getRowActions}
            renderActions={
              <Flex gap={8} wrap="wrap">
                {selectedReturnIds.length > 0 && canViewExportBatchReport && (
                  <Button
                    className="btn-success btn-sm"
                    type="button"
                    disabled={isPrintBatchPending || !selectedBatchNos.length}
                    onClick={() => handlePrintBatches(selectedBatchNos)}
                  >
                    {t("NUSUK_PRINT_SELECTED_BATCHES", {
                      count: selectedBatchNos.length,
                    })}
                  </Button>
                )}
              </Flex>
            }
          />
        </Stack>
      </Container>

      <ConfirmModal
        isOpen={deleteModal.open}
        onToggle={() => !isDeletePending && setDeleteModal({ open: false, row: null })}
        title={t("DELETE")}
        message={t("NUSUK_RETURN_DELETE_CONFIRM")}
        isLoading={isDeletePending}
        onConfirm={async () => {
          const row = deleteModal.row;
          const returnId = row ? getReturnId(row) : null;
          const batchId = row ? getReturnBatchId(row) : null;
          if (!returnId || !batchId) return;
          try {
            await deleteReturn({ batchId, returnId });
            toast.success(t("CHANGES_SAVED_SUCCESSFULLY"));
            setDeleteModal({ open: false, row: null });
            refetch();
          } catch {
            /* toast handled in mutation */
          }
        }}
      />
    </div>
  );
};

export default NusukCardsBatchReturns;
